/**
 * 파일명 : com0108m.tsx
 * 설명 : 휴가목록 화면
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용      
 ************************************************
 * 1.0       2020-08-10   윤성수     최초작성
 *     
 */

/************************************************
 * Import 영역  
 ************************************************/
/*  React  */
import React from 'react';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Com0108mAction } from '../../../redux/slice/com/com0108m_slice';
import { IOffItemState } from '../../../redux/models/com/com0108m_model';

/*  AntD  */
import { Button, Space, Row, Col, Layout, Typography, Table } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

/*  업무  */
import * as common from 'libs/common';


const Com0108m = () => {
    /************************************************
     * 화면 초기화 영역  
     ************************************************/
    /**
     * 기능 : State / Reducer 선언
     * param : 
     */
    const dispatch = useAppDispatch();
    const offList  = useAppSelector((state) => state.Com0108m.offList);
    const current  = useAppSelector((state) => state.Com0108m.current);
    const pageSize = useAppSelector((state) => state.Com0108m.pageSize);


    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     */
    React.useEffect(() => {
        fn_Search();
    }, []);


    /* Render 변수 선언영역 */
    /* AntD */
    const { Content } = Layout;
    const { Text }    = Typography;

    /* Table Columns */
    const columns = React.useCallback(() => {
        const arr:any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>휴가구분코드</div>,
                dataIndex: 'cd',
                key: 'cd',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>휴가구분명</div>,
                dataIndex: 'cdNm',
                key: 'cdNm',
                align: 'center',
                width: '20%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>공가여부</div>,
                dataIndex: 'attr1',
                key: 'attr1',
                align: 'center',
                width: '10%',
                render: (text:string) => text === 'Y' ? '공가' : '비공가'
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>반차여부</div>,
                dataIndex: 'attr2',
                key: 'attr2',
                align: 'center',
                width: '10%',
                render: (item:string) => {
                    let value:string;
                    if (item === '1') value = '연차';
                    if (item === '2') value = '오전반차';
                    if (item === '3') value = '오후반차';
                    return value;
                } 
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>사용여부</div>,
                dataIndex: 'useYn',
                key: 'useYn',
                align: 'center',
                width: '10%',
                render: (text:string) => text === 'Y' ? '사용' : '미사용'
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>상세보기</div>,
                key: 'action',
                render: (text:string, record:IOffItemState) => (
                    <Button
                        id='btn_showDetail'
                        name='btn_showDetail'
                        className='background-color-default-button'
                        size='small'
                        onClick={(e) => onClick(e, record)}
                    >
                        상세보기
                    </Button>
                ),
                align: 'center',
                width: '9%',
            }];
            return arr;
    }, []);

    /**
     * 기능 : 초기화 이벤트 선언
     * param : 
     */
    const fn_Reset = () => {
        dispatch(Com0108mAction.initState());
    };

    /**
     * 기능 : 결과초기화 이벤트 선언
     * param : 
     */
    const fn_ResetResult = () => {
        dispatch(Com0108mAction.initState());
    };


    /**
     * 기능 : onClick 이벤트 선언
     * param : 
     */
    const onClick = async (e:any, record:IOffItemState) => {
        const buttonName = e.currentTarget.name;
        switch (buttonName) {
            case 'btn_Search': {
                fn_Search();
                break;
            }

            case 'btn_Reset': {
                fn_Reset();
                break;
            }

            case 'btn_addOffr': {
                const params:any = {};
                params.title = '휴가등록';
                params.width = 800
                params.popupId = 'com0108p';
                params.accept = () => fn_Search();
                params.cancel = () => fn_Search();
                common.openPopup(params);
                break;

            }
            case 'btn_showDetail': {
                const params:any = {};
                params.title = '휴가관리';
                params.width = 800
                params.popupId = 'com0108p';
                params.accept = () => fn_Search();
                params.cancel = () => fn_Search();
                params.props = { cd : record.cd };
                common.openPopup(params);
                break;
            }

            default:
        }
    };

    /**
     * 기능 : 검색조회
     * param : 
     */
    const fn_Search = () => {
        dispatch(Com0108mAction.setCurrent(1));

        const option = {
            useYn: 'ALL',
            callback: (datas:IOffItemState[]) => {
                dispatch(Com0108mAction.setOffList(datas));
            }
        };
        common.getComCodeList('008', option);
    };


    return (
        <>
            <Layout>
                <Content>
                    <Row align='middle' style={{ height: 50 }}>
                        <Col flex='9.5' >
                            <Space>
                                <img className='title-icon' alt='' />
                                <Text strong>휴가목록</Text>
                            </Space>
                        </Col>
                        <Col flex='0.5' style={{ marginRight: 15 }}>
                            <Space>
                                <Button
                                    name='btn_Reset'
                                    className='background-color-default-button'
                                    onClick={(e:any) => onClick(e, null)}
                                >
                                    초기화
                                </Button>
                                <Button
                                    name='btn_Search'
                                    className='background-color-primary-button'
                                    icon={<SearchOutlined />}
                                    onClick={(e:any) => onClick(e, null)}
                                >
                                    조회
                                </Button>
                                {common.isAdmin() ?
                                <Button
                                    className='background-color-primary-button'
                                    onClick={(e:any) => onClick(e, null)}
                                    name='btn_addOffr'
                                >
                                    + 휴가등록
                                </Button> : null}
                            </Space>
                        </Col>
                    </Row>
                    <Table dataSource={offList}
                        key='cd'
                        columns={columns()}
                        size='small'
                        style={{width:'100%'}}
                        scroll={{y:550}}
                        pagination={{
                            pageSize: pageSize,
                            current: current,
                            showTotal : () => '총 ' + offList.length + ' 건',
                            onChange: (page, pageSize) => {
                                dispatch(Com0108mAction.setCurrent(page));
                                dispatch(Com0108mAction.setPageSize(pageSize));
                            },
                        }}
                    />
                </Content>
            </Layout>
        </>
    );
};

export default Com0108m;

